import { useState, useRef, useEffect } from "react";
import { useInstitutions } from "@/lib/useInstitutions";
import { useNavigate, useParams } from "react-router-dom";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { COUNTIES, PROPERTY_TYPES, CURRENCIES, money, INSTITUTION_TYPES, AMENITIES, SHORT_TERM_ADDONS, LISTER_TYPES, GENDER_PREF, isSharedType } from "@/data/constants";
import { MapPicker } from "@/components/MapPicker";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ImagePlus, Video, X, Loader2, ChevronRight, ChevronLeft, Check, MapPin, Home, Phone,
} from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Basics", "Location", "Details", "Media", "Price & Contact"];

export default function CreateListing() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const photoInput = useRef(null);
  const videoInput = useRef(null);
  const floorInput = useRef(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [feePreview, setFeePreview] = useState(null);
  const [customAmenity, setCustomAmenity] = useState("");

  const [form, setForm] = useState({
    title: "",
    listing_type: "rent",
    property_type: "",
    county: "",
    town: "",
    estate: "",
    area_map_hint: "",
    size: "",
    is_storey: false,
    floor_number: "",
    price: "",
    currency: "KSH",
    description: "",
    photos: [],
    video: "",
    floor_plan: "",
    gender_pref: "any",
    lister_type: "landlord",
    lat: null,
    lng: null,
    category: "general",
    institution: "",
    institution_type: "university",
    amenities: [],
    distance_km: "",
    roommates: "",
    check_in_time: "",
    check_out_time: "",
    min_nights: "",
    max_nights: "",
    st_addons: [],
    contact_name: "",
    contact_phone: "",
    contact_email: "",
  });

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const { institutions, addInstitution } = useInstitutions();
  const [newInst, setNewInst] = useState("");
  const [addingInst, setAddingInst] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    let ignore = false;
    (async () => {
      try {
        const { data } = await api.get("/my/listings");
        if (ignore) return;
        const l = data.find((x) => x.id === id);
        if (l) {
          setForm({
            title: l.title, listing_type: l.listing_type, property_type: l.property_type,
            county: l.county, town: l.town, estate: l.estate || "", area_map_hint: l.area_map_hint || "",
            size: l.size || "", is_storey: l.is_storey, floor_number: l.floor_number ?? "",
            price: l.price, currency: l.currency, description: l.description || "",
            photos: l.photos || [], video: l.video || "", floor_plan: l.floor_plan || "", gender_pref: l.gender_pref || "any", lister_type: l.lister_type || "landlord", lat: l.lat ?? null, lng: l.lng ?? null,
            category: l.category || "general", institution: l.institution || "", institution_type: l.institution_type || "university",
            amenities: l.amenities || [], distance_km: l.distance_km ?? "", roommates: l.roommates ?? "",
            check_in_time: l.check_in_time || "", check_out_time: l.check_out_time || "",
            min_nights: l.min_nights ?? "", max_nights: l.max_nights ?? "", st_addons: l.st_addons || [],
            contact_name: l.contact?.name || "", contact_phone: l.contact?.phone || "", contact_email: l.contact?.email || "",
          });
        }
      } catch {
        /* noop */
      }
    })();
    return () => {
      ignore = true;
    };
  }, [isEdit, id]);

  const uploadFiles = async (files, isVideo) => {
    if (isVideo && files.length) {
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", files[0]);
        const { data } = await api.post("/upload", fd);
        set("video", data.path);
        toast.success("Video uploaded");
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      } finally {
        setUploading(false);
      }
      return;
    }
    const remaining = 20 - form.photos.length;
    const chosen = Array.from(files).slice(0, remaining);
    if (files.length > remaining) toast.warning(`Only ${remaining} more photo(s) allowed (max 20)`);
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of chosen) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd);
        uploaded.push(data.path);
      }
      setForm((p) => ({ ...p, photos: [...p.photos, ...uploaded] }));
      toast.success(`${uploaded.length} photo(s) uploaded`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const uploadFloorPlan = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", files[0]);
      const { data } = await api.post("/upload", fd);
      set("floor_plan", data.path);
      toast.success("Floor plan uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const refreshFee = async () => {
    if (!form.price) return;
    try {
      const { data } = await api.get("/meta/fee-preview", {
        params: { price: form.price, currency: form.currency, listing_type: form.listing_type },
      });
      setFeePreview(data.unlock_fee);
    } catch {
      setFeePreview(null);
    }
  };

  const validStep = () => {
    if (step === 0) return form.title && form.property_type && form.listing_type;
    if (step === 1) return form.county && form.town;
    if (step === 4) return form.price && form.contact_name && form.contact_phone;
    return true;
  };

  const next = () => {
    if (!validStep()) {
      setError("Please fill in the required fields.");
      return;
    }
    setError("");
    if (step === 3) refreshFee();
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!validStep()) {
      setError("Please fill in the required fields.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        distance_km: form.category === "student" && form.distance_km !== "" ? Number(form.distance_km) : null,
        roommates: form.category === "student" && form.roommates !== "" ? Number(form.roommates) : null,
        min_nights: form.listing_type === "short_term" && form.min_nights !== "" ? Number(form.min_nights) : null,
        max_nights: form.listing_type === "short_term" && form.max_nights !== "" ? Number(form.max_nights) : null,
        floor_number: form.is_storey && form.floor_number !== "" ? Number(form.floor_number) : null,
      };
      const { data } = isEdit
        ? await api.patch(`/listings/${id}`, payload)
        : await api.post("/listings", payload);
      toast.success(isEdit ? "Listing updated!" : "Listing published!");
      navigate(`/listing/${data.id}`);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-5 py-10 lg:px-8">
      <span className="label-caps text-[#D4A373]">{isEdit ? "Edit listing" : "New listing"}</span>
      <h1 className="mt-2 font-display text-3xl font-medium tracking-tight text-[#1A1A1A]">
        {isEdit ? "Update your property" : "List your property"}
      </h1>

      {/* Stepper */}
      <div className="mt-8 flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex flex-1 items-center gap-2">
            <div
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors ${
                i < step ? "bg-[#D4A373] text-white" : i === step ? "bg-[#2C3830] text-white" : "bg-white text-[#6B7280]"
              }`}
            >
              {i < step ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            {i < STEPS.length - 1 && (
              <div className={`h-0.5 flex-1 ${i < step ? "bg-[#D4A373]" : "bg-border"}`} />
            )}
          </div>
        ))}
      </div>
      <p className="mt-3 font-display text-lg text-[#2C3830]">{STEPS[step]}</p>

      <div className="mt-6 rounded-2xl border border-border/60 bg-white p-6">
        {step === 0 && (
          <div className="space-y-5">
            <div className="space-y-2">
              <Label className="label-caps text-[#6B7280]">Listing category *</Label>
              <div className="flex gap-3">
                {[{ v: "general", l: "General" }, { v: "student", l: "Student housing" }].map((c) => (
                  <button
                    key={c.v}
                    type="button"
                    data-testid={`category-${c.v}`}
                    onClick={() => set("category", c.v)}
                    className={`flex-1 rounded-xl border p-4 text-sm font-medium transition-colors ${
                      form.category === c.v ? "border-[#2C3830] bg-[#2C3830] text-white" : "border-border text-[#2C3830] hover:border-[#2C3830]"
                    }`}
                  >
                    {c.l}
                  </button>
                ))}
              </div>
            </div>
            {form.category === "student" && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="label-caps text-[#6B7280]">Institution</Label>
                  <Select value={form.institution || "none"} onValueChange={(v) => set("institution", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="field-institution" className="rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Not specific</SelectItem>{institutions.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="flex gap-2 pt-1">
                    <Input
                      data-testid="new-institution-input"
                      value={newInst}
                      onChange={(e) => setNewInst(e.target.value)}
                      placeholder="Can't find it? Add your institution"
                      className="rounded-xl text-sm"
                    />
                    <Button
                      type="button"
                      data-testid="add-institution-btn"
                      variant="outline"
                      className="rounded-xl"
                      disabled={newInst.trim().length < 2 || addingInst}
                      onClick={async () => {
                        setAddingInst(true);
                        try {
                          const name = await addInstitution(newInst.trim());
                          set("institution", name);
                          setNewInst("");
                          toast.success("Institution added");
                        } catch (err) {
                          toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add institution");
                        } finally {
                          setAddingInst(false);
                        }
                      }}
                    >
                      Add
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-caps text-[#6B7280]">Institution type</Label>
                  <Select value={form.institution_type} onValueChange={(v) => set("institution_type", v)}>
                    <SelectTrigger data-testid="field-institution-type" className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>{INSTITUTION_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label className="label-caps text-[#6B7280]">Listing title *</Label>
              <Input data-testid="field-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Spacious 2BR in Kilimani" className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-[#6B7280]">Listing for *</Label>
              <div className="grid grid-cols-3 gap-3">
                {[{ v: "rent", l: "For Rent" }, { v: "sale", l: "For Sale" }, { v: "short_term", l: "Short-term" }].map((t) => (
                  <button
                    key={t.v}
                    data-testid={`listing-type-${t.v}`}
                    onClick={() => set("listing_type", t.v)}
                    className={`rounded-xl border p-4 text-sm font-medium transition-colors ${
                      form.listing_type === t.v ? "border-[#2C3830] bg-[#2C3830] text-white" : "border-border text-[#2C3830] hover:border-[#2C3830]"
                    }`}
                  >
                    {t.l}
                  </button>
                ))}
              </div>
              <div className="space-y-2">
                <Label className="label-caps text-[#6B7280]">You are listing as</Label>
                <Select value={form.lister_type} onValueChange={(v) => set("lister_type", v)}>
                  <SelectTrigger data-testid="lister-type-select" className="rounded-xl"><SelectValue placeholder="Select your role" /></SelectTrigger>
                  <SelectContent>
                    {LISTER_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value} data-testid={`lister-${t.value}`}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#6B7280]">Short-term rentals are usually listed by a Host. Get verified from your dashboard to earn a trust badge.</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-[#6B7280]">Property type *</Label>
              <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                <SelectTrigger data-testid="field-property-type" className="rounded-xl"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5">
            <div className="space-y-2">
              <Label className="label-caps text-[#6B7280]">County *</Label>
              <Select value={form.county} onValueChange={(v) => set("county", v)}>
                <SelectTrigger data-testid="field-county" className="rounded-xl"><SelectValue placeholder="Select county" /></SelectTrigger>
                <SelectContent>
                  {COUNTIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-caps text-[#6B7280]">Town *</Label>
                <Input data-testid="field-town" value={form.town} onChange={(e) => set("town", e.target.value)} placeholder="e.g. Westlands" className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label className="label-caps text-[#6B7280]">Estate / Neighbourhood</Label>
                <Input data-testid="field-estate" value={form.estate} onChange={(e) => set("estate", e.target.value)} placeholder="e.g. Riverside" className="rounded-xl" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-[#6B7280]">General area / map hint</Label>
              <Textarea data-testid="field-map-hint" value={form.area_map_hint} onChange={(e) => set("area_map_hint", e.target.value)} placeholder="e.g. Off Waiyaki Way, near ABC Place" className="rounded-xl" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps text-[#6B7280]">Pin the general area (optional)</Label>
              <MapPicker
                lat={form.lat}
                lng={form.lng}
                county={form.county}
                onChange={(la, ln) => setForm((p) => ({ ...p, lat: la, lng: ln }))}
              />
              <p className="text-xs text-[#6B7280]">
                Tap the map to drop a pin for the general area.
                {form.lat != null && (
                  <button type="button" data-testid="clear-pin" onClick={() => setForm((p) => ({ ...p, lat: null, lng: null }))} className="ml-2 underline">Clear pin</button>
                )}
              </p>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <div className="space-y-2">
              <Label className="label-caps text-[#6B7280]">Size / dimensions</Label>
              <Input data-testid="field-size" value={form.size} onChange={(e) => set("size", e.target.value)} placeholder="e.g. 90 sqm / 50x100 plot" className="rounded-xl" />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border p-4">
              <div>
                <p className="font-medium text-[#2C3830]">On a storey building?</p>
                <p className="text-sm text-[#6B7280]">Toggle if the unit is on a floor</p>
              </div>
              <Switch data-testid="field-is-storey" checked={form.is_storey} onCheckedChange={(v) => set("is_storey", v)} />
            </div>
            {form.is_storey && (
              <div className="space-y-2">
                <Label className="label-caps text-[#6B7280]">Floor number</Label>
                <Input data-testid="field-floor" type="number" value={form.floor_number} onChange={(e) => set("floor_number", e.target.value)} placeholder="e.g. 3" className="rounded-xl" />
              </div>
            )}
            <div className="space-y-2">
              <Label className="label-caps text-[#6B7280]">Description</Label>
              <Textarea data-testid="field-description" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Describe the property, amenities, terms…" rows={5} className="rounded-xl" />
            </div>
            {isSharedType(form.property_type) && (
              <div className="space-y-2">
                <Label className="label-caps text-[#6B7280]">Roommate gender preference</Label>
                <p className="text-xs text-[#6B7280]">For shared rooms, beds or common areas — choose who this space is for.</p>
                <Select value={form.gender_pref} onValueChange={(v) => set("gender_pref", v)}>
                  <SelectTrigger data-testid="field-gender-pref" className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{GENDER_PREF.map((g) => <SelectItem key={g.value} value={g.value} data-testid={`gender-${g.value}`}>{g.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {form.category === "student" && (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="label-caps text-[#6B7280]">Distance to campus (km)</Label>
                    <Input data-testid="field-distance" type="number" step="0.1" value={form.distance_km} onChange={(e) => set("distance_km", e.target.value)} placeholder="e.g. 1.5" className="rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps text-[#6B7280]">Roommates (if shared)</Label>
                    <Input data-testid="field-roommates" type="number" min="0" max="8" value={form.roommates} onChange={(e) => set("roommates", e.target.value)} placeholder="e.g. 4" className="rounded-xl" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-caps text-[#6B7280]">Amenities</Label>
                  <div className="flex flex-wrap gap-2">
                    {AMENITIES.map((a) => (
                      <button key={a} type="button" data-testid={`field-amenity-${a}`} onClick={() => set("amenities", form.amenities.includes(a) ? form.amenities.filter((x) => x !== a) : [...form.amenities, a])} className={`rounded-full border px-3 py-1 text-xs transition-colors ${form.amenities.includes(a) ? "border-[#2C3830] bg-[#2C3830] text-white" : "border-border text-[#6B7280] hover:border-[#2C3830]"}`}>{a}</button>
                    ))}
                  </div>
                </div>
              </>
            )}
            {form.listing_type === "short_term" && (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="label-caps text-[#6B7280]">Check-in time</Label>
                    <Input data-testid="field-checkin" type="time" value={form.check_in_time} onChange={(e) => set("check_in_time", e.target.value)} className="rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps text-[#6B7280]">Check-out time</Label>
                    <Input data-testid="field-checkout" type="time" value={form.check_out_time} onChange={(e) => set("check_out_time", e.target.value)} className="rounded-xl" />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="label-caps text-[#6B7280]">Min nights</Label>
                    <Input data-testid="field-min-nights" type="number" min="1" value={form.min_nights} onChange={(e) => set("min_nights", e.target.value)} placeholder="1" className="rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps text-[#6B7280]">Max nights (up to 30)</Label>
                    <Input data-testid="field-max-nights" type="number" min="1" max="30" value={form.max_nights} onChange={(e) => set("max_nights", e.target.value)} placeholder="30" className="rounded-xl" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-caps text-[#6B7280]">Amenities</Label>
                  <p className="text-xs text-[#6B7280]">Tap the amenities available at your unit. Add any that aren't listed.</p>
                  <div className="flex flex-wrap gap-2">
                    {[...SHORT_TERM_ADDONS, ...form.st_addons.filter((a) => !SHORT_TERM_ADDONS.includes(a))].map((a) => (
                      <button key={a} type="button" data-testid={`field-addon-${a}`} onClick={() => set("st_addons", form.st_addons.includes(a) ? form.st_addons.filter((x) => x !== a) : [...form.st_addons, a])} className={`rounded-full border px-3 py-1 text-xs transition-colors ${form.st_addons.includes(a) ? "border-[#2C3830] bg-[#2C3830] text-white" : "border-border text-[#6B7280] hover:border-[#2C3830]"}`}>{a}</button>
                    ))}
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Input
                      data-testid="custom-amenity-input"
                      value={customAmenity}
                      onChange={(e) => setCustomAmenity(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const v = customAmenity.trim();
                          if (v && !form.st_addons.includes(v)) set("st_addons", [...form.st_addons, v]);
                          setCustomAmenity("");
                        }
                      }}
                      placeholder="Add another amenity…"
                      className="rounded-xl"
                    />
                    <Button
                      type="button"
                      data-testid="add-amenity-btn"
                      variant="outline"
                      className="rounded-xl"
                      onClick={() => {
                        const v = customAmenity.trim();
                        if (v && !form.st_addons.includes(v)) set("st_addons", [...form.st_addons, v]);
                        setCustomAmenity("");
                      }}
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div>
              <Label className="label-caps text-[#6B7280]">Photos (up to 20)</Label>
              <input ref={photoInput} type="file" accept="image/*" multiple hidden onChange={(e) => uploadFiles(e.target.files, false)} />
              <div className="mt-2 grid grid-cols-3 gap-3 sm:grid-cols-4">
                {form.photos.map((p, i) => (
                  <div key={i} className="group relative aspect-square overflow-hidden rounded-xl border border-border">
                    <img src={fileUrl(p)} alt="" className="h-full w-full object-cover" />
                    <button
                      data-testid={`remove-photo-${i}`}
                      onClick={() => set("photos", form.photos.filter((_, idx) => idx !== i))}
                      className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {form.photos.length < 20 && (
                  <button
                    data-testid="add-photos-btn"
                    onClick={() => photoInput.current?.click()}
                    disabled={uploading}
                    className="flex aspect-square flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-border text-[#6B7280] hover:border-[#D4A373] hover:text-[#D4A373]"
                  >
                    {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                    <span className="text-xs">Add</span>
                  </button>
                )}
              </div>
              <p className="mt-2 text-xs text-[#6B7280]">{form.photos.length}/20 photos</p>
            </div>

            <div>
              <Label className="label-caps text-[#6B7280]">Video (1 max)</Label>
              <input ref={videoInput} type="file" accept="video/*" hidden onChange={(e) => uploadFiles(e.target.files, true)} />
              {form.video ? (
                <div className="mt-2 flex items-center justify-between rounded-xl border border-border p-3">
                  <span className="flex items-center gap-2 text-sm text-[#2C3830]"><Video className="h-4 w-4 text-[#D4A373]" /> Video uploaded</span>
                  <button data-testid="remove-video-btn" onClick={() => set("video", "")} className="text-[#6B7280] hover:text-destructive"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <button
                  data-testid="add-video-btn"
                  onClick={() => videoInput.current?.click()}
                  disabled={uploading}
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border py-4 text-sm text-[#6B7280] hover:border-[#D4A373] hover:text-[#D4A373]"
                >
                  {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Video className="h-5 w-5" />}
                  Upload a walkthrough video
                </button>
              )}
            </div>

            <div>
              <Label className="label-caps text-[#6B7280]">Floor plan (optional)</Label>
              <input ref={floorInput} type="file" accept="image/*" hidden onChange={(e) => uploadFloorPlan(e.target.files)} />
              {form.floor_plan ? (
                <div className="mt-2 flex items-center justify-between rounded-xl border border-border p-3">
                  <span className="flex items-center gap-2 text-sm text-[#2C3830]"><ImagePlus className="h-4 w-4 text-[#D4A373]" /> Floor plan uploaded</span>
                  <div className="flex items-center gap-3">
                    <a href={fileUrl(form.floor_plan)} target="_blank" rel="noreferrer" className="text-xs text-[#2C3830] underline">View</a>
                    <button data-testid="remove-floor-plan-btn" onClick={() => set("floor_plan", "")} className="text-[#6B7280] hover:text-destructive"><X className="h-4 w-4" /></button>
                  </div>
                </div>
              ) : (
                <button
                  data-testid="add-floor-plan-btn"
                  onClick={() => floorInput.current?.click()}
                  disabled={uploading}
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border py-4 text-sm text-[#6B7280] hover:border-[#D4A373] hover:text-[#D4A373]"
                >
                  {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                  Upload a floor plan image
                </button>
              )}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-5">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-2">
                <Label className="label-caps text-[#6B7280]">Price *</Label>
                <Input data-testid="field-price" type="number" value={form.price} onChange={(e) => set("price", e.target.value)} onBlur={refreshFee} placeholder="e.g. 45000" className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label className="label-caps text-[#6B7280]">Currency</Label>
                <Select value={form.currency} onValueChange={(v) => { set("currency", v); }}>
                  <SelectTrigger data-testid="field-currency" className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {feePreview != null && (
              <div data-testid="fee-preview" className="rounded-xl bg-[#F7F5F0] p-4 text-sm text-[#2C3830]">
                Interested users will pay a platform access fee of{" "}
                <span className="font-semibold">{money(feePreview, form.currency)}</span> to unlock your contact
                ({form.listing_type === "sale" ? "0.1%" : "0.25%"} of {form.listing_type === "short_term" ? "the nightly price" : "listing price"}).
              </div>
            )}

            <div className="rounded-xl border border-border p-4">
              <p className="mb-3 flex items-center gap-2 font-medium text-[#2C3830]"><Phone className="h-4 w-4 text-[#D4A373]" /> Contact (hidden until unlocked)</p>
              <div className="space-y-3">
                <Input data-testid="field-contact-name" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} placeholder="Contact name *" className="rounded-xl" />
                <Input data-testid="field-contact-phone" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} placeholder="Phone number *" className="rounded-xl" />
                <Input data-testid="field-contact-email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="Email (optional)" className="rounded-xl" />
              </div>
            </div>
          </div>
        )}

        {error && <p data-testid="create-error" className="mt-4 text-sm text-destructive">{error}</p>}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <Button data-testid="step-back" variant="ghost" onClick={back} disabled={step === 0} className="rounded-full text-[#6B7280]">
          <ChevronLeft className="mr-1 h-4 w-4" /> Back
        </Button>
        {step < STEPS.length - 1 ? (
          <Button data-testid="step-next" onClick={next} className="rounded-full bg-[#2C3830] px-8 text-white hover:bg-[#3A4A40]">
            Continue <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        ) : (
          <Button data-testid="publish-btn" onClick={submit} disabled={saving} className="rounded-full bg-[#D4A373] px-8 text-white hover:bg-[#c39364]">
            {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {isEdit ? "Saving…" : "Publishing…"}</> : <><Home className="mr-2 h-4 w-4" /> {isEdit ? "Save changes" : "Publish listing"}</>}
          </Button>
        )}
      </div>
    </div>
  );
}
